/**
 * Google's `subscriptionNotification.notificationType` integers.
 *
 * The receiver stores the integer as text, verbatim and unmapped, so the only
 * record of what a row means is this table. Reading `12` in a list of events
 * tells nobody that a merchant's subscription was revoked.
 *
 * @see https://developer.android.com/google/play/billing/rtdn-reference
 */
const PLAY_SUBSCRIPTION_TYPES: ReadonlyMap<string, string> = new Map([
  ["1", "SUBSCRIPTION_RECOVERED"],
  ["2", "SUBSCRIPTION_RENEWED"],
  ["3", "SUBSCRIPTION_CANCELED"],
  ["4", "SUBSCRIPTION_PURCHASED"],
  ["5", "SUBSCRIPTION_ON_HOLD"],
  ["6", "SUBSCRIPTION_IN_GRACE_PERIOD"],
  ["7", "SUBSCRIPTION_RESTARTED"],
  ["8", "SUBSCRIPTION_PRICE_CHANGE_CONFIRMED"],
  ["9", "SUBSCRIPTION_DEFERRED"],
  ["10", "SUBSCRIPTION_PAUSED"],
  ["11", "SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED"],
  ["12", "SUBSCRIPTION_REVOKED"],
  ["13", "SUBSCRIPTION_EXPIRED"],
  ["20", "SUBSCRIPTION_PENDING_PURCHASE_CANCELED"],
]);

/**
 * A store notification type as a person should read it.
 *
 * Apple's types are already words and pass through. Google's subscription types
 * are integers and are named here; its other bodies — `VOIDED_PURCHASE`,
 * `ONE_TIME_PRODUCT`, `TEST` — are words the receiver assigned and also pass
 * through.
 *
 * An unrecognised Play integer keeps its raw value in the label rather than
 * being hidden behind "unknown": the number is the only thing that can be looked
 * up against Google's reference when they add one.
 */
export function storeNotificationLabel(
  provider: string,
  notificationType: string | null | undefined,
): string {
  if (!notificationType) return "—";
  if (provider !== "google") return notificationType;
  if (!/^\d+$/.test(notificationType)) return notificationType;

  return (
    PLAY_SUBSCRIPTION_TYPES.get(notificationType) ??
    `Play type ${notificationType}`
  );
}
